import { useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import awsRumService from '../services/awsRum';

/**
 * React Hook for AWS RUM Integration
 * 
 * This hook provides easy access to AWS RUM functionality within React components
 * and automatically tracks page navigation and component lifecycle events.
 */
export const useAwsRum = (componentName = 'Unknown') => {
    const location = useLocation();
    const navigate = useNavigate();
    const previousLocation = useRef(location.pathname);
    const componentMountTime = useRef(Date.now());

    // Initialize RUM on first use
    useEffect(() => {
        const initializeRum = async () => {
            if (!awsRumService.isAvailable()) {
                await awsRumService.initialize();
            }
        };
        
        initializeRum();
    }, []);

    // Track page navigation
    useEffect(() => {
        if (awsRumService.isAvailable()) {
            const currentPath = location.pathname;
            const previousPath = previousLocation.current;

            if (currentPath !== previousPath) {
                awsRumService.trackPageNavigation(previousPath, currentPath, {
                    componentName: componentName,
                    search: location.search,
                    hash: location.hash
                });

                // Track funnel step based on page
                const funnelSteps = {
                    '/': 'landing',
                    '/registration': 'registration',
                    '/checkout': 'checkout',
                    '/payment': 'payment',
                    '/upload-selfie': 'selfie_upload',
                    '/upload-license': 'license_upload',
                    '/sms-verification': 'sms_verification',
                    '/thank-you': 'completion'
                };

                const funnelStep = funnelSteps[currentPath];
                if (funnelStep) {
                    awsRumService.trackFunnelStep(funnelStep, {
                        componentName: componentName,
                        previousStep: funnelSteps[previousPath] || 'unknown'
                    });
                }

                previousLocation.current = currentPath;
            }
        }
    }, [location, componentName]);

    // Track component mount/unmount
    useEffect(() => {
        if (awsRumService.isAvailable()) {
            awsRumService.recordEvent('component_mount', {
                componentName: componentName,
                page: location.pathname,
                timestamp: new Date().toISOString()
            });
        }

        return () => {
            if (awsRumService.isAvailable()) {
                const mountDuration = Date.now() - componentMountTime.current;
                awsRumService.recordEvent('component_unmount', {
                    componentName: componentName,
                    page: location.pathname,
                    mountDuration: mountDuration,
                    timestamp: new Date().toISOString()
                });
            }
        };
    }, [componentName, location.pathname]);

    // =============================================================================
    // HOOK METHODS
    // =============================================================================

    /**
     * Track a custom event
     */
    const trackEvent = useCallback((eventName, eventData = {}) => {
        if (awsRumService.isAvailable()) {
            awsRumService.recordEvent(eventName, {
                componentName: componentName,
                page: location.pathname,
                ...eventData
            });
        }
    }, [componentName, location.pathname]);

    /**
     * Track user interaction
     */
    const trackInteraction = useCallback((action, context = {}) => {
        if (awsRumService.isAvailable()) {
            awsRumService.trackUserInteraction(action, {
                componentName: componentName,
                ...context
            });
        }
    }, [componentName]);

    /**
     * Track form submission
     */
    const trackFormSubmission = useCallback((formName, formData = {}) => {
        if (awsRumService.isAvailable()) {
            awsRumService.recordEvent('form_submission', {
                formName: formName,
                componentName: componentName,
                page: location.pathname,
                ...formData,
                timestamp: new Date().toISOString()
            });
        }
    }, [componentName, location.pathname]);

    /**
     * Track form validation errors
     */
    const trackFormErrors = useCallback((formName, errors = []) => {
        if (awsRumService.isAvailable()) {
            awsRumService.trackFormValidationErrors(formName, errors);
        }
    }, []);

    /**
     * Track API call
     */
    const trackApiCall = useCallback((endpoint, method, duration, status, success, context = {}) => {
        if (awsRumService.isAvailable()) {
            awsRumService.trackApiCall(endpoint, method, duration, status, success);
            
            // Also track as custom event with component context
            awsRumService.recordEvent('api_call_from_component', {
                endpoint: endpoint,
                method: method,
                duration: duration,
                status: status,
                success: success,
                componentName: componentName,
                ...context
            });
        }
    }, [componentName]);

    /**
     * Track error
     */
    const trackError = useCallback((error, context = {}) => {
        if (awsRumService.isAvailable()) {
            awsRumService.trackError(error, {
                componentName: componentName,
                ...context
            });
        }
    }, [componentName]);

    /**
     * Track performance metric
     */
    const trackPerformance = useCallback((metricName, value, unit = 'ms', context = {}) => {
        if (awsRumService.isAvailable()) {
            awsRumService.trackPerformanceMetric(metricName, value, unit, {
                componentName: componentName,
                ...context
            });
        }
    }, [componentName]);

    /**
     * Track payment events
     */
    const trackPayment = {
        start: useCallback((paymentData = {}) => {
            if (awsRumService.isAvailable()) {
                awsRumService.trackPaymentStart({
                    componentName: componentName,
                    ...paymentData
                });
            }
        }, [componentName]),

        validation: useCallback((validationData = {}) => {
            if (awsRumService.isAvailable()) {
                awsRumService.trackPaymentValidation({
                    componentName: componentName,
                    ...validationData
                });
            }
        }, [componentName]),

        success: useCallback((successData = {}) => {
            if (awsRumService.isAvailable()) {
                awsRumService.trackPaymentSuccess({
                    componentName: componentName,
                    ...successData
                });
            }
        }, [componentName]),

        error: useCallback((errorData = {}) => {
            if (awsRumService.isAvailable()) {
                awsRumService.trackPaymentError({
                    componentName: componentName,
                    ...errorData
                });
            }
        }, [componentName])
    };

    /**
     * Track Square SDK events
     */
    const trackSquare = useCallback((eventType, eventData = {}) => {
        if (awsRumService.isAvailable()) {
            awsRumService.trackSquareEvent(eventType, {
                componentName: componentName,
                ...eventData
            });
        }
    }, [componentName]);

    /**
     * Add user attributes
     */
    const addUserAttributes = useCallback((attributes) => {
        if (awsRumService.isAvailable()) {
            awsRumService.addUserAttributes(attributes);
        }
    }, []);

    /**
     * Add session attributes
     */
    const addSessionAttributes = useCallback((attributes) => {
        if (awsRumService.isAvailable()) {
            awsRumService.addSessionAttributes(attributes);
        }
    }, []);

    /**
     * Track drop-off
     */
    const trackDropOff = useCallback((dropOffPoint, context = {}) => {
        if (awsRumService.isAvailable()) {
            awsRumService.trackDropOff(dropOffPoint, {
                componentName: componentName,
                ...context
            });
        }
    }, [componentName]);

    /**
     * Get session ID
     */
    const getSessionId = useCallback(() => {
        if (awsRumService.isAvailable()) {
            return awsRumService.getSessionId();
        }
        return null;
    }, []);

    /**
     * Check if RUM is available
     */
    const isRumAvailable = useCallback(() => {
        return awsRumService.isAvailable();
    }, []);

    return {
        // Core tracking methods
        trackEvent,
        trackInteraction,
        trackFormSubmission,
        trackFormErrors,
        trackApiCall,
        trackError,
        trackPerformance,
        trackDropOff,

        // Specialized tracking
        trackPayment,
        trackSquare,

        // User and session management
        addUserAttributes,
        addSessionAttributes,
        getSessionId,

        // Utility
        isRumAvailable,

        // Direct access to service (for advanced use cases)
        rumService: awsRumService
    };
};

export default useAwsRum;